import { useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import type { InsightsDailyRevenuePoint } from "@shared-core/data/api-types";
import {
  blendedMarginPct,
  formatGbpCompact,
  niceAxisMax,
  peakDay,
  projectDailyRevenue,
  quotedRevenueMultiple,
  totalQuotedValue,
  totalRevenue,
  type DailyRevenueDatum,
  type RevenueBasis,
} from "@shared-core/domain/insights-daily-revenue";

// Daily revenue + contribution over the trailing ~3 months, with a
// booked/invoiced toggle, the estimate/quoting overlay (Quoted value line on a
// second/right axis, breaking on zero/weekend days), and month divider lines +
// labels. Faithful to the settled design reference
// (docs/features/bi-native-analysis/charts/daily-l3m.html).
//
// Server composes, client renders: this component only projects the fields the
// endpoint already returns (revenue_performance_daily ⋈ quoted_value_daily); it
// performs no aggregation.

const REVENUE_COLOR = "var(--color-chart-1)";
const CONTRIBUTION_COLOR = "var(--color-chart-2)";
const QUOTED_COLOR = "#b45309";

export interface DailyRevenueChartProps {
  days: InsightsDailyRevenuePoint[];
  /** Initial basis; defaults to booked (the design's default). */
  defaultBasis?: RevenueBasis;
}

export function DailyRevenueChart({
  days,
  defaultBasis = "booked",
}: DailyRevenueChartProps) {
  const [basis, setBasis] = useState<RevenueBasis>(defaultBasis);
  const data = projectDailyRevenue(days, basis);

  if (days.length === 0) {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-12 text-center text-sm">
        No daily revenue data for this period yet.
      </div>
    );
  }

  const basisWord = basis === "booked" ? "booked" : "invoiced";
  const peak = peakDay(data);
  const quotedMult = quotedRevenueMultiple(data);

  // The quoting line breaks on zero (weekend / quiet) days: set quotedLine to
  // null there so recharts (connectNulls={false}) leaves a gap rather than
  // diving to 0.
  const chartData = data.map((d) => ({
    ...d,
    quotedLine: d.quotedValue > 0 ? d.quotedValue : null,
  }));
  // Explicit 0-floored axis maxima so neither axis dips negative (see
  // niceAxisMax): left covers the revenue/contribution bars, right the quoted
  // line. Both use the same tick count, so their gridlines stay aligned.
  const revenueMax = niceAxisMax(
    Math.max(0, ...data.map((d) => Math.max(d.revenue, d.contribution))),
  );
  const quotingMax = niceAxisMax(
    Math.max(0, ...data.map((d) => d.quotedValue)),
  );
  // Month-boundary anchors for the divider lines + labels.
  const monthStarts = data.filter((d) => d.isMonthStart);

  const rhythm =
    basis === "invoiced"
      ? "Invoiced revenue is lumpy — big spikes at month-end when invoices are batched, and near-nothing in the first days of a month."
      : "Booked revenue is fairly even across working days — order intake doesn't wait for month-end the way invoicing does.";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-foreground text-base font-semibold">
            Revenue, contribution &amp; estimate demand — daily
          </h2>
          <p className="text-muted-foreground text-sm">
            Whole company · {basisWord} basis · last {days.length} days
          </p>
        </div>
        <BasisToggle basis={basis} onChange={setBasis} />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi
          label={`${days.length}-day ${basisWord} revenue`}
          value={formatGbpCompact(totalRevenue(data))}
        />
        <Kpi
          label="Contribution margin"
          value={`${Math.round(blendedMarginPct(data))}%`}
          sub={`blended, ${basisWord}`}
          tone="good"
        />
        <Kpi
          label="Quoted value 3m"
          value={formatGbpCompact(totalQuotedValue(data))}
          sub={`deduped · ${quotedMult.toFixed(1)}× revenue`}
        />
        <Kpi
          label="Peak day"
          value={peak ? formatGbpCompact(peak.revenue) : "£0"}
          sub={peak ? `${peak.label} (${peak.weekday})` : undefined}
        />
      </div>

      <div className="border-border bg-surface rounded-lg border p-4">
        <ChartLegend basisWord={basisWord} />
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
            barGap={0}
          >
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            {monthStarts.map((d) => (
              <ReferenceLine
                key={d.day}
                yAxisId="revenue"
                x={d.label}
                stroke="var(--color-border)"
                strokeDasharray="2 3"
                label={{
                  value: d.monthLabel,
                  position: "insideTopLeft",
                  fill: "var(--color-muted-foreground)",
                  fontSize: 10,
                }}
              />
            ))}
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
              axisLine={{ stroke: "var(--color-border)" }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            {/* Explicit numeric [0, max] domains + a shared tick count keep
                both axes 0-floored and their gridlines aligned. A numeric
                floor is the only thing recharts honours on a dual-axis chart —
                with domain={[0, "auto"]} its nice-tick pass re-centres the
                domain to align the two axes and pushes the left one below zero,
                reading as fake negative revenue. See niceAxisMax.
                allowDataOverflow pins the floor to exactly 0: a few quiet days
                carry a marginally-negative contribution (rounding/adjustments),
                and without it recharts would expand the domain to ~-£40 to fit
                them, printing a stray negative tick. Those sub-£100 bars clip
                harmlessly at the zero line — matching the design reference,
                which only drew positive contribution bars. */}
            <YAxis
              yAxisId="revenue"
              domain={[0, revenueMax]}
              allowDataOverflow
              tickCount={5}
              tickFormatter={formatGbpCompact}
              tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              width={52}
            />
            <YAxis
              yAxisId="quoting"
              orientation="right"
              domain={[0, quotingMax]}
              allowDataOverflow
              tickCount={5}
              tickFormatter={formatGbpCompact}
              tick={{ fontSize: 10, fill: QUOTED_COLOR }}
              axisLine={false}
              tickLine={false}
              width={52}
            />
            <Tooltip
              cursor={{ fill: "var(--color-muted)", opacity: 0.3 }}
              content={DailyTooltip as never}
            />
            <Bar
              yAxisId="revenue"
              dataKey="revenue"
              name="Revenue"
              fill={REVENUE_COLOR}
              isAnimationActive={false}
            />
            <Bar
              yAxisId="revenue"
              dataKey="contribution"
              name="Contribution"
              fill={CONTRIBUTION_COLOR}
              isAnimationActive={false}
            />
            <Line
              yAxisId="quoting"
              dataKey="quotedLine"
              name="Quoted value"
              stroke={QUOTED_COLOR}
              strokeWidth={2}
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <p className="text-muted-foreground mt-3 text-xs">
          <span className="text-foreground font-medium">
            {basis === "invoiced" ? "Invoiced" : "Booked"} basis.
          </span>{" "}
          {rhythm} Weekends drop to near-zero (the gaps). Daily quoted value
          (amber, right axis) runs about {quotedMult.toFixed(1)}× daily revenue
          over the period. Toggle basis to see how much smoother booking is than
          invoicing.
        </p>
        <p className="text-muted-foreground mt-2 text-xs">
          Revenue/contribution ={" "}
          {basis === "booked"
            ? "booked amounts by order date"
            : "invoiced amounts by invoice date"}
          , per day. Quoted value = deduped estimate header ref (all outcomes)
          by estimate date; gross (pre-dedupe) is in the tooltip. Recent days'
          invoiced totals lag (invoices batched later). Window: {data[0].label}{" "}
          – {data[data.length - 1].label}.
        </p>
      </div>
    </div>
  );
}

function BasisToggle({
  basis,
  onChange,
}: {
  basis: RevenueBasis;
  onChange: (b: RevenueBasis) => void;
}) {
  return (
    <div
      className="bg-muted inline-flex rounded-lg p-[3px]"
      role="group"
      aria-label="Revenue basis"
    >
      {(["booked", "invoiced"] as const).map((b) => (
        <button
          key={b}
          type="button"
          aria-pressed={basis === b}
          onClick={() => onChange(b)}
          className={
            "rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors " +
            (basis === b
              ? "bg-card text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground")
          }
        >
          {b}
        </button>
      ))}
    </div>
  );
}

function ChartLegend({ basisWord }: { basisWord: string }) {
  return (
    <div className="text-muted-foreground mb-3 flex flex-wrap items-center gap-4 text-xs">
      <LegendSwatch color={REVENUE_COLOR} label={`Revenue (${basisWord})`} />
      <LegendSwatch color={CONTRIBUTION_COLOR} label="Contribution" />
      <LegendLine color={QUOTED_COLOR} label="Quoted value — deduped" />
    </div>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-3 w-3 rounded-sm"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

function LegendLine({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-0 w-4"
        style={{ borderTop: `3px solid ${color}` }}
      />
      {label}
    </span>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
  className,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "good";
  className?: string;
}) {
  const toneClass =
    tone === "good" ? "text-[var(--color-chart-2)]" : "text-foreground";
  return (
    <div
      className={
        "border-border bg-surface rounded-lg border p-3 " + (className ?? "")
      }
    >
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      <div
        className={`mt-1.5 font-mono text-xl font-semibold tabular-nums ${toneClass}`}
      >
        {value}
      </div>
      {sub ? (
        <div className="text-muted-foreground mt-0.5 text-xs">{sub}</div>
      ) : null}
    </div>
  );
}

function DailyTooltip(props: TooltipContentProps<number, string>) {
  if (!props.active || !props.payload || props.payload.length === 0) {
    return null;
  }
  const datum = props.payload[0]?.payload as DailyRevenueDatum | undefined;
  if (!datum) return null;
  return (
    <div
      className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="mb-1 font-semibold">
        {datum.label}{" "}
        <span className="text-muted-foreground">{datum.weekday}</span>
        {datum.isWeekend ? (
          <span className="text-muted-foreground ml-1">· weekend</span>
        ) : null}
      </div>
      <TooltipRow
        color={REVENUE_COLOR}
        label="Revenue"
        value={formatGbpCompact(datum.revenue)}
      />
      <TooltipRow
        color={CONTRIBUTION_COLOR}
        label="Contribution"
        value={formatGbpCompact(datum.contribution)}
      />
      <div className="text-muted-foreground mt-0.5 mb-1">
        Margin {datum.marginPct.toFixed(1)}%
      </div>
      <TooltipRow
        color={QUOTED_COLOR}
        label="Quoted value"
        value={formatGbpCompact(datum.quotedValue)}
      />
      <div className="text-muted-foreground mt-0.5 flex items-center justify-between gap-4">
        <span>Gross (pre-dedupe)</span>
        <span className="font-mono tabular-nums">
          {formatGbpCompact(datum.grossQuoted)}
        </span>
      </div>
    </div>
  );
}

function TooltipRow({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block h-2 w-2 rounded-sm"
          style={{ backgroundColor: color }}
        />
        {label}
      </span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}
